'use client';

import { useOrganizationList } from '@clerk/clerk-react';
import * as React from 'react';

/** Single Clerk memberships query for the whole CRM shell — avoids duplicate XHRs. */
export const ORG_MEMBERSHIPS_QUERY = {
  userMemberships: {
    infinite: true,
    pageSize: 100,
  },
} as const;

const useOrgMembershipsQuery = () => useOrganizationList(ORG_MEMBERSHIPS_QUERY);

type ClerkOrgMembershipsValue = ReturnType<typeof useOrgMembershipsQuery>;

type OrgMembershipsValue = ClerkOrgMembershipsValue & {
  /** Opt-in driver that paginates through every remaining page; idempotent. */
  loadAllPages: () => void;
  /** True while the eager-pagination driver still has work to do. */
  isLoadingAllPages: boolean;
};

const OrgMembershipsContext = React.createContext<OrgMembershipsValue | null>(null);

export const OrgMembershipsProvider = ({ children }: { children: React.ReactNode }) => {
  const clerkValue = useOrgMembershipsQuery();
  const [shouldLoadAll, setShouldLoadAll] = React.useState(false);

  const loadAllPages = React.useCallback(() => {
    setShouldLoadAll(true);
  }, []);

  const { isLoaded, userMemberships } = clerkValue;
  const hasNextPage = userMemberships?.hasNextPage ?? false;
  const isFetching = userMemberships?.isFetching ?? false;
  const dataLength = userMemberships?.data?.length ?? 0;

  // Ref keeps the latest Clerk hook handle without making the effect re-fire on identity churn.
  const userMembershipsRef = React.useRef(userMemberships);
  userMembershipsRef.current = userMemberships;

  // Drives Clerk's paginated `fetchNext` until `hasNextPage` flips. `dataLength` is the
  // re-trigger after each page lands — without it the effect would skip when `hasNextPage`
  // stays `true → true` between pages.
  React.useEffect(() => {
    if (!shouldLoadAll) return;
    if (!isLoaded) return;
    if (!hasNextPage) return;
    if (isFetching) return;
    userMembershipsRef.current?.fetchNext?.();
  }, [shouldLoadAll, isLoaded, hasNextPage, isFetching, dataLength]);

  const isLoadingAllPages = shouldLoadAll && (isFetching || hasNextPage);

  const value = React.useMemo<OrgMembershipsValue>(
    () => ({ ...clerkValue, loadAllPages, isLoadingAllPages }),
    [clerkValue, loadAllPages, isLoadingAllPages],
  );

  return (
    <OrgMembershipsContext.Provider value={value}>{children}</OrgMembershipsContext.Provider>
  );
};

export const useOrgMemberships = (): OrgMembershipsValue => {
  const context = React.useContext(OrgMembershipsContext);
  if (!context) {
    throw new Error('useOrgMemberships must be used within OrgMembershipsProvider');
  }
  return context;
};
